import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  useTheme,
  Avatar,
} from "@mui/material";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import CloseIcon from "@mui/icons-material/Close";
import { FileUploadOutlined } from "@mui/icons-material";
import { getCroppedImg } from "../utils/ImageCrop";
import { tokens } from "../theme";

interface PhotoEditorProps {
  initialImage: string | null;
  onSave: (imageData: string) => void;
  title?: string;
}

const PhotoEditor: React.FC<PhotoEditorProps> = ({
  initialImage,
  onSave,
  title,
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(initialImage);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [open, setOpen] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);

  useEffect(() => {
    setPreviewImage(initialImage);
  }, [initialImage]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result as string);
      setOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!image || !croppedAreaPixels) return;
    const croppedImage = await getCroppedImg(image, croppedAreaPixels);
    setPreviewImage(croppedImage);
    onSave(croppedImage);
    setOpen(false);
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box>
      {/* Hidden File Input */}
      <input
        accept="image/png,image/jpeg,image/jpg"
        style={{ display: "none" }}
        id="upload-deceased-image"
        type="file"
        onChange={handleAvatarChange}
      />

      {/* Clickable Upload Section */}
      <Box
        component="label"
        htmlFor="upload-deceased-image"
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{
          border: `1px solid ${colors.primary[300]}`,
          borderRadius: "8px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: colors.primary[800],
          },
        }}
      >
        {/* Thumbnail Avatar */}
        {previewImage && (
          <Avatar
            src={previewImage}
            alt="Deceased"
            sx={{ width: 50, height: 50 }}
            onClick={(e) => {
              e.preventDefault(); // prevent file picker
              setPreviewDialog(true);
            }}
          />
        )}

        <Typography
          variant="body1"
          flexGrow={1}
          sx={{ color: colors.primary[300] }}
        >
          {previewImage ? "Image selected" : title}
        </Typography>

        <IconButton component="span" sx={{ color: colors.primary[300] }}>
          <FileUploadOutlined />
        </IconButton>
      </Box>

      {/* Cropping Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {title}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", height: 400 }}>
          {image && (
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button sx={{ color: "#724E41" }} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            sx={{ backgroundColor: "#724E41" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          Image Preview
          <IconButton
            onClick={() => setPreviewDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center" }}>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Full Preview"
              sx={{ maxWidth: "100%" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoEditor;
